import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBillingOverview } from '../../../hooks/useBilling';
import { RANGE_OPTIONS, useBillingRange } from '../../../hooks/useBillingRange';
import { CompactSegmentedToggle, type CompactSegmentedOption } from './CompactSegmentedToggle';
import { KpiCard, OverviewCard } from './OverviewCard';
import {
    AgingChart,
    CategoryDonut,
    ChartLegend,
    CollectionRateChart,
    MonthlyCollectedChart,
    MonthlyStackedChart,
    STATUS_CHART_COLORS
} from './OverviewCharts';
import { RangeControl } from './RangeControl';
import {
    NeedsAttentionList,
    RecentActivityList,
    TopDebtorsList,
    TopPracticesList
} from './OverviewRails';
import { formatMoneyDollars as money } from '../../../utils/money';
import type { OverviewActivityItem, OverviewAttentionItem } from '../../../services/billing';

// The full billing-overview dashboard body -- KPI strip, every chart/rail --
// shared by the org-wide OverviewDashboard page and the Accounts practice
// detail Billing tab. `practiceId` scopes every aggregation server-side (see
// GET /api/v2/billing/overview); omitted means org-wide. Owns its own range
// state so the org-wide page and an embedded, practice-scoped instance never
// fight over the same sessionStorage key -- callers pass a unique
// `rangeStorageKey`. Does not touch the page header/title; callers that need
// the range label in their header can read it via `onRangeLabelChange`.

type MonthlyBucket = 'due' | 'created';

function loadMonthlyBucket(storageKey: string): MonthlyBucket {
    try {
        const raw = sessionStorage.getItem(storageKey);
        return raw === 'created' ? 'created' : 'due';
    } catch {
        return 'due';
    }
}

function signedPct(value: number): string {
    return `${value > 0 ? '+' : ''}${value}%`;
}

const STACK_LEGEND = [
    { label: 'Paid', color: STATUS_CHART_COLORS.paid },
    { label: 'Open', color: STATUS_CHART_COLORS.open },
    { label: 'Overdue', color: STATUS_CHART_COLORS.overdue },
    { label: 'Void', color: STATUS_CHART_COLORS.void }
];

const MONTHLY_BUCKET_OPTIONS: readonly CompactSegmentedOption<MonthlyBucket>[] = [
    { key: 'due', label: 'Due date' },
    { key: 'created', label: 'Created' }
];

interface MonthlyBucketToggleProps {
    active: MonthlyBucket;
    onChange: (next: MonthlyBucket) => void;
}

function MonthlyBucketToggle({ active, onChange }: MonthlyBucketToggleProps) {
    return (
        <CompactSegmentedToggle
            options={MONTHLY_BUCKET_OPTIONS}
            active={active}
            onChange={onChange}
            ariaLabel="Group invoices by due date or creation date"
        />
    );
}

interface OverviewContentProps {
    // Scopes every aggregation to one practice; omitted means org-wide.
    practiceId?: string;
    // sessionStorage key for the range control -- must be unique per mounted
    // instance (the org-wide page and an embedded practice tab use different
    // keys so their ranges persist independently).
    rangeStorageKey: string;
    onRangeLabelChange?: (label: string) => void;
}

export function OverviewContent({
    practiceId,
    rangeStorageKey,
    onRangeLabelChange
}: OverviewContentProps) {
    const navigate = useNavigate();
    const monthlyBucketStorageKey = `${rangeStorageKey}__monthlyBucket`;

    const [monthlyBucket, setMonthlyBucket] = useState<MonthlyBucket>(() =>
        loadMonthlyBucket(monthlyBucketStorageKey)
    );

    useEffect(() => {
        try {
            sessionStorage.setItem(monthlyBucketStorageKey, monthlyBucket);
        } catch {
            /* */
        }
    }, [monthlyBucket, monthlyBucketStorageKey]);

    const { range, setRange, rangeLabel } = useBillingRange(rangeStorageKey, RANGE_OPTIONS, '12M');
    const { overview, isLoading, isFetching } = useBillingOverview(range, practiceId);

    useEffect(() => {
        onRangeLabelChange?.(rangeLabel);
    }, [rangeLabel, onRangeLabelChange]);

    const openEntity = (item: OverviewAttentionItem | OverviewActivityItem) => {
        if (item.entityType === 'charge') navigate(`/billing/charges/${item.entityId}`);
        else if (item.entityType === 'invoice') navigate(`/billing/invoices/${item.entityId}`);
        else if (item.entityType === 'one_time_discount') navigate('/billing/one-time-discounts');
    };

    const avgCollectionRate = useMemo(() => {
        const rates = (overview?.collections ?? [])
            .map((point) => point.ratePct)
            .filter((rate): rate is number => rate !== null);
        if (rates.length === 0) return null;
        return Math.round(rates.reduce((sum, rate) => sum + rate, 0) / rates.length);
    }, [overview]);

    if (isLoading && !overview) {
        return (
            <div className="flex items-center justify-center py-24">
                <div className="flex items-center gap-2 text-gray-400">
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                    <span className="text-sm">Loading billing overview…</span>
                </div>
            </div>
        );
    }

    if (!overview) {
        return (
            <div className="flex items-center justify-center py-24">
                <p className="text-sm text-gray-500">Failed to load the billing overview.</p>
            </div>
        );
    }

    const { kpis } = overview;

    return (
        <div className="flex flex-col gap-4">
            {/* Range control */}
            <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 pl-1 text-sm text-gray-500">
                    <span className="font-semibold text-gray-700">Billing analytics</span>
                    <span className="text-gray-300">·</span>
                    <span>{rangeLabel}</span>
                    {isFetching && !isLoading && (
                        <span className="text-xs text-gray-400">Refreshing…</span>
                    )}
                </div>
                <RangeControl active={range} onChange={setRange} options={RANGE_OPTIONS} />
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
                <KpiCard
                    label="Invoiced"
                    info="Total amount of invoices created in the selected range. Voided invoices are excluded."
                    value={money(kpis.invoiced)}
                    delta={kpis.invoicedDeltaPct !== null ? signedPct(kpis.invoicedDeltaPct) : null}
                    deltaTone={
                        kpis.invoicedDeltaPct !== null && kpis.invoicedDeltaPct < 0 ? 'down' : 'up'
                    }
                    foot="vs. prior period"
                />
                <KpiCard
                    label="Collected"
                    info="Total amount of invoices paid within the range. The collection rate is collected ÷ invoiced."
                    value={money(kpis.collected)}
                    delta={kpis.collectionRatePct !== null ? `${kpis.collectionRatePct}%` : null}
                    deltaTone="neutral"
                    foot="collection rate"
                />
                <KpiCard
                    label="Outstanding"
                    info="Open and partially paid invoices as of today, whatever range is selected."
                    value={money(kpis.outstanding)}
                    foot={`across ${kpis.outstandingCount} invoice${kpis.outstandingCount === 1 ? '' : 's'}`}
                />
                <KpiCard
                    label="Avg. days to pay"
                    info="Average time from invoice creation to payment, over invoices paid in the range. Lower is better."
                    value={kpis.avgDaysToPay !== null ? `${kpis.avgDaysToPay}d` : '-'}
                    delta={
                        kpis.avgDaysToPayDelta !== null
                            ? `${kpis.avgDaysToPayDelta > 0 ? '+' : ''}${kpis.avgDaysToPayDelta}d`
                            : null
                    }
                    // Paying faster than the prior period is the good direction.
                    deltaTone={
                        kpis.avgDaysToPayDelta !== null && kpis.avgDaysToPayDelta <= 0
                            ? 'up'
                            : 'down'
                    }
                    foot="vs. prior period"
                />
                <KpiCard
                    label="One-time discounts issued"
                    info="One-time discounts created in the range. 'Applied' is the part already linked to a charge and deducted from its invoices."
                    value={money(kpis.oneTimeDiscountsIssued)}
                    foot={`${money(kpis.oneTimeDiscountsApplied)} applied`}
                />
            </div>

            {/* Invoiced trend */}
            <OverviewCard
                title="Invoices by month"
                info="Invoice amounts stacked by their current status (paid, open, overdue, void), grouped into months by creation or due date - pick which with the toggle."
                action={
                    <div className="flex items-center gap-3">
                        <ChartLegend items={STACK_LEGEND} />
                        <MonthlyBucketToggle active={monthlyBucket} onChange={setMonthlyBucket} />
                    </div>
                }
            >
                <MonthlyStackedChart data={overview.monthly[monthlyBucket]} />
            </OverviewCard>

            {/* Practice-scoped: one combined analytics row - Top practices and
          Top debtors are both leaderboards of one under a practiceId filter,
          so they are omitted. */}
            {practiceId ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
                    <OverviewCard
                        title="Collection rate"
                        info="Per month: amount paid that month ÷ amount invoiced that month. Months with nothing invoiced show no point; the reference line is the range average."
                    >
                        <CollectionRateChart data={overview.collections} avg={avgCollectionRate} />
                    </OverviewCard>
                    <OverviewCard
                        title="Collected by month"
                        info="Amount actually paid each month, by payment date - the dollar figure behind the collection rate."
                        action={
                            <span className="text-[13px] font-semibold text-gray-900 tabular-nums">
                                {money(kpis.collected)}
                            </span>
                        }
                    >
                        <MonthlyCollectedChart data={overview.collections} />
                    </OverviewCard>
                    <OverviewCard
                        title="A/R aging"
                        info="Outstanding (open or partially paid) invoices grouped by how far past their due date they are today. 'Current' means not yet due."
                        action={
                            <span className="text-[13px] font-semibold text-gray-900 tabular-nums">
                                {money(kpis.outstanding)} open
                            </span>
                        }
                    >
                        <AgingChart data={overview.aging} />
                    </OverviewCard>
                    <OverviewCard
                        title="Revenue by category"
                        info="Invoiced amounts in the range split by the product category of each charge's line items."
                    >
                        <CategoryDonut data={overview.byCategory} />
                    </OverviewCard>
                </div>
            ) : (
                <>
                    {/* Collections + aging */}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        <OverviewCard
                            title="Collection rate"
                            info="Per month: amount paid that month ÷ amount invoiced that month. Months with nothing invoiced show no point; the reference line is the range average."
                        >
                            <CollectionRateChart
                                data={overview.collections}
                                avg={avgCollectionRate}
                            />
                        </OverviewCard>
                        <OverviewCard
                            title="Collected by month"
                            info="Amount actually paid each month, by payment date - the dollar figure behind the collection rate."
                            action={
                                <span className="text-[13px] font-semibold text-gray-900 tabular-nums">
                                    {money(kpis.collected)}
                                </span>
                            }
                        >
                            <MonthlyCollectedChart data={overview.collections} />
                        </OverviewCard>
                        <OverviewCard
                            title="A/R aging"
                            info="Outstanding (open or partially paid) invoices grouped by how far past their due date they are today. 'Current' means not yet due."
                            action={
                                <span className="text-[13px] font-semibold text-gray-900 tabular-nums">
                                    {money(kpis.outstanding)} open
                                </span>
                            }
                        >
                            <AgingChart data={overview.aging} />
                        </OverviewCard>
                    </div>

                    {/* Category / practices / debtors */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <OverviewCard
                            title="Revenue by category"
                            info="Invoiced amounts in the range split by the product category of each charge's line items."
                        >
                            <CategoryDonut data={overview.byCategory} />
                        </OverviewCard>
                        <OverviewCard
                            title="Top practices"
                            info="Practices ranked by amount invoiced in the selected range."
                            action={<span className="text-[11px] text-gray-400">this range</span>}
                        >
                            <TopPracticesList items={overview.topPractices} />
                        </OverviewCard>
                        <OverviewCard
                            title="Top debtors"
                            info="Practices ranked by what they still owe right now: open and partially paid invoices as of today, whatever range is selected. The rose part of each bar is already past its due date."
                            action={<span className="text-[11px] text-gray-400">as of today</span>}
                        >
                            <TopDebtorsList items={overview.topDebtors} />
                        </OverviewCard>
                    </div>
                </>
            )}

            {/* Operational rail */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                    <OverviewCard
                        title="Needs attention"
                        info="Items an admin should act on: failed charges and invoices, one-time discounts expiring soon, and charges stuck in pending."
                        action={
                            <span className="text-[13px] font-semibold text-gray-400">
                                {overview.needsAttention.length} item
                                {overview.needsAttention.length === 1 ? '' : 's'}
                            </span>
                        }
                    >
                        <div className="h-[320px] overflow-y-auto scrollbar-hide">
                            <NeedsAttentionList
                                items={overview.needsAttention}
                                onOpen={openEntity}
                            />
                        </div>
                    </OverviewCard>
                </div>
                <OverviewCard
                    title="Recent activity"
                    info="The latest billing events - charge and invoice creations, payments, status syncs from Bill.com, voids, and manual edits."
                >
                    <div className="h-[320px] overflow-y-auto scrollbar-hide">
                        <RecentActivityList items={overview.recent} onOpen={openEntity} />
                    </div>
                </OverviewCard>
            </div>
        </div>
    );
}
